import {
  useAuthStore,
  useIsAdminOrManagerRole,
  useWorkspaceKeysStore,
  useWorkspaceStore,
} from '@easy-expense/data-firestore-client';
import { Statement } from '@easy-expense/data-schema-v2';
import { WebEnv } from '@easy-expense/env-mobile';
import Data from '@easy-expense/frontend-data-layer';
import { getTranslation, useIntlStore } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans } from '@easy-expense/ui-web-core';
import { dateFormatSkeleton, getUserDisplayName } from '@easy-expense/utils-shared';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '../Button.components';
import { TableTextSize } from '../Shared/TableTextSize';

type StatementWithData = Statement & {
  assignedToDisplayName: string;
  dateDisplay: string; // Month + Year format
};

export const StatementListTable: React.FC = () => {
  const navigate = useNavigate();

  const currentUser = useAuthStore((state) => state.currentUser);
  const isManager = useIsAdminOrManagerRole();
  const statementsRaw = Data.statements.use();
  // TODO statements should be filtered server side and with firebase rules EZ-3485
  // Memoize so the filtered array keeps a stable reference across renders. Without
  // this, Material React Table receives a new `data` reference every render and
  // enters an infinite render loop that freezes the Reconcile tab.
  const statements = React.useMemo(() => {
    if (!Array.isArray(statementsRaw)) {
      return [];
    }
    return statementsRaw.filter((statement) => {
      if (!currentUser) {
        return false;
      }
      if (isManager) {
        return true;
      }

      return (
        statement.createdBy === currentUser.uid || statement.assignedTo.includes(currentUser.uid)
      );
    });
  }, [statementsRaw, currentUser, isManager]);

  const members = useWorkspaceStore((s) => s.workspaceMembers);
  const { formatDate } = useIntlStore();

  function getStatementDate(statement: Statement): Date {
    if (statement.dateRange?.min) {
      return new Date(statement.dateRange.min);
    }
    if (statement.createdAt) {
      return new Date(statement.createdAt * 1000);
    }
    return new Date();
  }

  function formatStatementDate(date: Date): string {
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    return formatDate(date, {
      skeleton: dateFormatSkeleton.monthYear,
    });
  }

  function getAssignedToDisplayName(statement: Statement): string {
    if (!statement.assignedTo || statement.assignedTo.length === 0) {
      return '—';
    }
    const assignedMemberNames = statement.assignedTo
      .map((userID) => {
        const member = members?.[userID];
        return getUserDisplayName(member, 'Unknown');
      })
      .filter((name) => name !== 'Unknown');

    return assignedMemberNames.length > 0 ? assignedMemberNames.join(', ') : '—';
  }

  function processStatement(statement: Statement): StatementWithData {
    const assignedToDisplayName = getAssignedToDisplayName(statement);
    const dateObject = getStatementDate(statement);
    const dateDisplay = formatStatementDate(dateObject);

    return {
      ...statement,
      assignedToDisplayName,
      dateDisplay,
    };
  }

  const statementsWithData = React.useMemo<StatementWithData[]>(() => {
    if (!Array.isArray(statements) || statements.length === 0) {
      return [];
    }

    return statements.map((statement) => {
      try {
        return processStatement(statement);
      } catch (error) {
        console.error('Error processing statement:', statement, error);
        return {
          ...statement,
          assignedToDisplayName: '—',
          dateDisplay: 'Invalid Date',
        };
      }
    });
  }, [statements, members, formatDate]);

  function onRowClick(statement: StatementWithData) {
    navigate(`/statement/${statement.key}`);
  }

  const columns = React.useMemo<MRT_ColumnDef<StatementWithData>[]>(
    () => [
      {
        accessorKey: 'dateDisplay',
        header: getTranslation('Date'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.xs}>{getTranslation('Date')}</OpenSans.Secondary>
        ),
        size: 120,
        Cell: ({ row }) => {
          return (
            <Layout.Column style={{ width: 120 }}>
              <OpenSans.Primary size={TableTextSize.xs}>
                {row.original.dateDisplay}
              </OpenSans.Primary>
            </Layout.Column>
          );
        },
      },
      {
        accessorKey: 'name',
        header: getTranslation('Statement Name'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.xs}>
            {getTranslation('Statement Name')}
          </OpenSans.Secondary>
        ),
        size: 250,
        Cell: ({ row }) => {
          return (
            <Layout.Column style={{ width: 250 }}>
              <OpenSans.Primary size={TableTextSize.xs} weight="bold-700">
                {row.original.name}
              </OpenSans.Primary>
            </Layout.Column>
          );
        },
      },
      // Only admins/managers may see who statements are assigned to. Members would
      // otherwise see every workspace member's name here (EZ-3653).
      ...(isManager
        ? ([
            {
              accessorKey: 'assignedToDisplayName',
              header: getTranslation('Assigned To'),
              Header: () => (
                <OpenSans.Secondary size={'s-12'}>
                  {getTranslation('Assigned To')}
                </OpenSans.Secondary>
              ),
              size: 150,
              Cell: ({ row }) => {
                return (
                  <Layout.Column style={{ width: 150 }}>
                    <OpenSans.Primary size={'s-12'}>
                      {row.original.assignedToDisplayName}
                    </OpenSans.Primary>
                  </Layout.Column>
                );
              },
            },
          ] as MRT_ColumnDef<StatementWithData>[])
        : []),
      {
        accessorKey: 'reconciledCount',
        header: getTranslation('Matched'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.xs}>
            {getTranslation('Matched')}
          </OpenSans.Secondary>
        ),
        size: 150,
        Cell: ({ row }) => {
          return (
            <Layout.Column style={{ width: 150 }}>
              <OpenSans.Primary size={TableTextSize.xs} style={{ color: theme.colors.success }}>
                {row.original.reconciledCount}
              </OpenSans.Primary>
            </Layout.Column>
          );
        },
      },
      {
        accessorKey: 'missingCount',
        header: getTranslation('Missing'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.xs}>
            {getTranslation('Missing')}
          </OpenSans.Secondary>
        ),
        size: 150,
        Cell: ({ row }) => {
          return (
            <Layout.Column style={{ width: 150 }}>
              <OpenSans.Primary size={TableTextSize.xs} style={{ color: theme.colors.destructive }}>
                {row.original.missingCount}
              </OpenSans.Primary>
            </Layout.Column>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        Header: () => null,
        size: 200,
        Cell: ({ row }) => {
          return (
            <Layout.Column style={{ width: 200 }}>
              <Button.Secondary
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  navigate(`/statement/${row.original.key}`);
                }}
                content={getTranslation('Rerun Reconciliation')}
              />
            </Layout.Column>
          );
        },
      },
    ],
    [formatDate, navigate, isManager],
  );

  const table = useMaterialReactTable({
    columns,
    data: statementsWithData,
    initialState: {
      showGlobalFilter: true,
      density: 'compact',
      sorting: [{ desc: true, id: 'dateDisplay' }],
      pagination: { pageIndex: 0, pageSize: 25 },
    },
    icons: {
      CloseIcon: () => (
        <Icon
          name="close"
          size={18}
          color={theme.colors.secondary}
          style={{ padding: 0, margin: 0 }}
        />
      ),
      SearchIcon: () => (
        <Icon name="search" size={18} color={theme.colors.secondary} style={{ marginRight: 8 }} />
      ),
    },
    enableColumnActions: false,
    enableDensityToggle: false,
    getRowId: (row) => row.key,
    muiTableHeadCellProps: {
      sx: {
        '& .Mui-TableHeadCell-Content': {
          color: theme.colors.secondary,
          fontWeight: 'regular',
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => onRowClick(row.original),
      sx: {
        cursor: 'pointer',
      },
    }),
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: '0',
        border: '',
      },
    },
  });

  const currentWorkspacePath = useWorkspaceKeysStore((s) => s.currentWorkspacePath());
  const statementsCollectionPath = currentWorkspacePath ? `${currentWorkspacePath}/statements` : '';
  const firestoreUrl = statementsCollectionPath
    ? `https://console.firebase.google.com/project/${WebEnv.firebase.projectId}/firestore/data/~2F${statementsCollectionPath.replace(/\//g, '~2F')}`
    : '';

  return (
    <Layout.Column>
      {firestoreUrl && __DEV__ && (
        <Layout.Row justify="flex-end" style={{ marginBottom: 8 }}>
          <OpenSans.Secondary
            size="s-14"
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => window.open(firestoreUrl, '_blank')}
          >
            Open in Firebase Console
          </OpenSans.Secondary>
        </Layout.Row>
      )}
      <MaterialReactTable table={table} />
    </Layout.Column>
  );
};
